import { useIntl } from 'react-intl';
import {
    dateTimeColumn,
    type GridColDef,
    type GridValidRowModel
} from '../lib/dataGrid';
import { useUserLabel } from '../features/users/useUserLabel';
import { makeUserResolver } from '../features/users/makeUserResolver';
import { useUserDirectory } from '../features/users/userDirectory';

/** The audit stamps a row's doc carries — see `AUDIT_FIELDS` in ./auditFields. */
export interface AuditStamps {
    createdBy?: string;
    updatedBy?: string;
    createdAt?: string;
    updatedAt?: string;
}

/**
 * The four audit columns — Erstellt von / Geändert von / Erstellt am / Geändert
 * am — for an office grid, appended before its actions column.
 *
 * They make a record's provenance sortable and filterable in the grid itself;
 * the full field-by-field story stays in the history dialog. Hidden by default
 * via {@link HIDDEN_AUDIT_COLUMNS}.
 *
 * `createdBy`/`updatedBy` store a USER ID, so the column resolves it to the
 * display name in its `valueGetter` (not a renderer): sorting, filtering, the
 * quick filter and the export then all work on the name the cell shows. An id
 * with no user left behind it falls back to the raw id.
 *
 * `stampsOf` reads the stamps off a row for grids whose rows are not the doc
 * itself (the Befunde tree); flat entity grids pass nothing. Pass a
 * MODULE-LEVEL function, not an inline arrow, so the column set stays
 * referentially stable across renders.
 *
 * Deliberately not offered in the Feldaufnahme.
 */
export function useAuditColumns<Row extends GridValidRowModel>(
    stampsOf?: (row: Row) => AuditStamps | undefined
): GridColDef<Row>[] {
    const intl = useIntl();
    const { users } = useUserDirectory();
    const userName = useUserLabel();
    const resolveUser = makeUserResolver(users, userName);
    const stamps =
        stampsOf ?? ((row: Row) => row as unknown as AuditStamps | undefined);

    function userColumn(field: 'createdBy' | 'updatedBy'): GridColDef<Row> {
        return {
            field,
            headerName: intl.formatMessage({ id: `common.${field}` }),
            width: 160,
            valueGetter: (_value, row) => {
                const id = stamps(row)?.[field] ?? '';
                return id ? resolveUser(id) : '';
            }
        };
    }

    function timeColumn(field: 'createdAt' | 'updatedAt'): GridColDef<Row> {
        return dateTimeColumn<Row>({
            field,
            headerName: intl.formatMessage({ id: `common.${field}` }),
            width: 170,
            valueGetter: (_value, row) => stamps(row)?.[field] ?? ''
        });
    }

    return [
        userColumn('createdBy'),
        userColumn('updatedBy'),
        timeColumn('createdAt'),
        timeColumn('updatedAt')
    ];
}
